import { useState, useEffect, useCallback, useRef } from "react";
import { metFirma } from "@/lib/downloadUrl";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarBlank,
  Users,
  CaretLeft,
  CaretRight,
  FileXls,
  FilePdf,
  Buildings,
  ListBullets,
  Table as TableIcon,
  PencilSimple,
  Plus,
  Trash,
  Check,
  ArrowClockwise,
  CopySimple,
  ArrowRight,
  CheckCircle,
  XCircle,
  UserPlus,
  Circle,
  Scales,
  Airplane,
  Warning,
  ClockClockwise,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAGNAMEN_KORT = ["Ma", "Di", "Wo", "Do", "Vr", "Za", "Zo"];
const DAGNAMEN_LANG = [
  "Maandag", "Dinsdag", "Woensdag", "Donderdag",
  "Vrijdag", "Zaterdag", "Zondag",
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit" });
};
const fmtDateLang = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("nl-BE", { day: "2-digit", month: "long", year: "numeric" });
};

const mondayOf = (d) => {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7;
  x.setDate(x.getDate() - dow);
  return x;
};
const fmtIso = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

// ─── Status configuratie ──────────────────────────────────────────────────
const STATUS_CONFIG = {
  gepland: {
    label: "Gepland",
    icon: Circle,
    color: "#94A3B8",            // slate-400
    bg: "transparent",
    nameClass: "",
    iconWeight: "regular",
  },
  aanwezig: {
    label: "Aanwezig",
    icon: CheckCircle,
    color: "#16A34A",            // green-600
    bg: "#F0FAEC",
    nameClass: "",
    iconWeight: "fill",
  },
  afwezig: {
    label: "Afwezig",
    icon: XCircle,
    color: "#DC2626",            // red-600
    bg: "#FEF2F2",
    nameClass: "line-through text-[#DC2626]",
    iconWeight: "fill",
  },
  vervanger: {
    label: "Vervanger",
    icon: UserPlus,
    color: "#2563EB",            // blue-600
    bg: "#EFF6FF",
    nameClass: "italic",
    iconWeight: "fill",
  },
};
const STATUS_CYCLE = ["gepland", "aanwezig", "afwezig", "vervanger"];
const nextStatus = (cur) => {
  const idx = STATUS_CYCLE.indexOf(cur || "gepland");
  return STATUS_CYCLE[(idx + 1) % STATUS_CYCLE.length];
};

export default function Planning() {
  const today = new Date();
  const [view, setView] = useState("week"); // dag | week
  const [datum, setDatum] = useState(fmtIso(today));
  const [weekStart, setWeekStart] = useState(fmtIso(mondayOf(today)));
  const [filialen, setFilialen] = useState([]);
  const [filiaalFilter, setFiliaalFilter] = useState("all");

  const [dagData, setDagData] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [medewerkers, setMedewerkers] = useState([]);
  const [editCel, setEditCel] = useState(null);
  const [celVerlof, setCelVerlof] = useState({});
  // editCel: {filiaal_id, filiaal_naam, datum, weekdag, slots:[{medewerker_id, uren}]}

  useEffect(() => {
    axios.get(`${API}/filialen`).then((r) => setFilialen(r.data || []));
    axios.get(`${API}/medewerkers`).then((r) => setMedewerkers(r.data || []));
  }, []);

  const fetchDag = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/planning/dag`, { params: { datum } });
      setDagData(res.data);
    } catch {
      toast.error("Fout bij laden");
    } finally {
      setLoading(false);
    }
  }, [datum]);

  const fetchWeek = useCallback(async () => {
    setLoading(true);
    try {
      const params = { start_datum: weekStart };
      if (filiaalFilter !== "all") params.filiaal_id = filiaalFilter;
      const res = await axios.get(`${API}/planning/week`, { params });
      setWeekData(res.data);
    } catch {
      toast.error("Fout bij laden week");
    } finally {
      setLoading(false);
    }
  }, [weekStart, filiaalFilter]);

  useEffect(() => { if (view === "dag") fetchDag(); }, [view, fetchDag]);
  useEffect(() => { if (view === "week") fetchWeek(); }, [view, fetchWeek]);

  // Auto-refresh wanneer venster terug focus krijgt (bv. na invoer in andere tab)
  useEffect(() => {
    const onFocus = () => {
      if (view === "dag") fetchDag();
      else fetchWeek();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [view, fetchDag, fetchWeek]);

  // ── Navigatie ──
  const navDag = (delta) => {
    const d = new Date(datum);
    d.setDate(d.getDate() + delta);
    setDatum(fmtIso(d));
  };
  const navWeek = (delta) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + delta * 7);
    setWeekStart(fmtIso(mondayOf(d)));
  };
  const goToday = () => {
    setDatum(fmtIso(today));
    setWeekStart(fmtIso(mondayOf(today)));
  };

  // ── Exports ──
  const dl = (path, fname) => {
    const params = new URLSearchParams({ start_datum: weekStart });
    if (filiaalFilter !== "all") params.set("filiaal_id", filiaalFilter);
    const url = metFirma(`${API}${path}?${params.toString()}`);
    const a = document.createElement("a");
    a.href = url;
    a.download = fname;
    a.target = "_blank";
    a.click();
  };

  const dlFiliaal = (path, fil, ext) => {
    const params = new URLSearchParams({ start_datum: weekStart, filiaal_id: fil.filiaal_id });
    const slug = (fil.filiaal_naam || "filiaal").toLowerCase().replace(/[^a-z0-9]+/g, "-");
    const url = metFirma(`${API}${path}?${params.toString()}`);
    const a = document.createElement("a");
    a.href = url;
    a.download = `planning_${weekStart}_${slug}.${ext}`;
    a.target = "_blank";
    a.click();
    toast.success(`${fil.filiaal_naam} — ${ext.toUpperCase()} gedownload`);
  };

  const visibleFilialen = (fil) => {
    if (!fil) return [];
    if (filiaalFilter === "all") return fil;
    return fil.filter((f) => f.filiaal_id === filiaalFilter);
  };

  // ── Edit cell ──
  const openEditCel = (filiaal, dag) => {
    if (!editMode) return;
    setCelVerlof({});
    axios
      .get(`${API}/planning/verlof-op-dag`, { params: { datum: dag.datum } })
      .then((r) => setCelVerlof(r.data?.verlof || {}))
      .catch(() => setCelVerlof({}));
    setEditCel({
      filiaal_id: filiaal.filiaal_id,
      filiaal_naam: filiaal.filiaal_naam,
      datum: dag.datum,
      weekdag: dag.weekdag,
      slots: dag.medewerkers.map((m) => ({
        medewerker_id: m.medewerker_id,
        uren: String(m.uren),
        status: m.status || "gepland",
        vervanger_voor_id: m.vervanger_voor_id || null,
      })),
    });
  };

  const cellSetSlot = (idx, field, value) => {
    setEditCel((prev) => {
      const next = { ...prev, slots: [...prev.slots] };
      next.slots[idx] = { ...next.slots[idx], [field]: value };
      return next;
    });
  };
  const cellAddSlot = () => {
    setEditCel((prev) => ({
      ...prev,
      slots: [...prev.slots, { medewerker_id: "", uren: "8", status: "gepland" }],
    }));
  };
  const cellRemoveSlot = (idx) => {
    setEditCel((prev) => ({
      ...prev,
      slots: prev.slots.filter((_, i) => i !== idx),
    }));
  };

  const saveEditCel = async () => {
    if (!editCel) return;
    const slots = editCel.slots
      .filter((s) => s.medewerker_id && parseFloat(s.uren) > 0)
      .map((s) => ({
        medewerker_id: s.medewerker_id,
        filiaal_id: editCel.filiaal_id,
        datum: editCel.datum,
        uren: parseFloat(s.uren),
        status: s.status || "gepland",
        vervanger_voor_id: s.vervanger_voor_id || null,
      }));

    const metVerlof = slots.filter((s) => celVerlof[s.medewerker_id]);
    if (metVerlof.length > 0) {
      const namen = metVerlof
        .map((s) => {
          const m = medewerkers.find((x) => x.id === s.medewerker_id);
          const v = celVerlof[s.medewerker_id];
          return `• ${m ? `${m.voornaam} ${m.achternaam}` : s.medewerker_id} — ${v.type}${v.halve_dag ? " (½ dag)" : ""}`;
        })
        .join("\n");
      if (!window.confirm(
        `Let op: deze medewerker(s) hebben verlof op ${editCel.datum}:\n\n${namen}\n\nToch inplannen?`
      )) return;
    }

    try {
      await axios.post(`${API}/planning/cel`, {
        filiaal_id: editCel.filiaal_id,
        datum: editCel.datum,
        slots,
      });
      toast.success(`${slots.length} medewerker${slots.length === 1 ? "" : "s"} ingepland`);
      setEditCel(null);
      fetchWeek();
      if (view === "dag") fetchDag();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fout bij opslaan");
    }
  };

  // Wis een cel volledig (delete alle medewerkers van deze filiaal+datum)
  const clearCel = async (fil, dag) => {
    if (!dag || dag.aantal === 0) return;
    try {
      await axios.post(`${API}/planning/cel`, {
        filiaal_id: fil.filiaal_id,
        datum: dag.datum,
        slots: [],
      });
      toast.success("Cel gewist");
      fetchWeek();
      if (view === "dag") fetchDag();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wissen mislukt");
    }
  };

  // Cycle status van een individuele medewerker (gepland → aanwezig → afwezig → vervanger → gepland)
  const cycleStatus = async (rec) => {
    if (!rec?.id) return;
    const ns = nextStatus(rec.status);
    try {
      await axios.patch(`${API}/planning/${rec.id}/status`, { status: ns });
      // optimistic UI: refresh
      fetchWeek();
      if (view === "dag") fetchDag();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Status-update mislukt");
    }
  };

  // Automatisch vergelijken met Aanwezigheid (tot en met vandaag)
  const [autoVergeleken, setAutoVergeleken] = useState("");
  const autoRef = useRef(new Set());

  useEffect(() => {
    if (!weekData?.start_datum) return;
    const vandaag = new Date();
    const vandaagIso = new Date(vandaag.getTime() - vandaag.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0];
    if (weekData.start_datum > vandaagIso) return; // toekomstige week: niets te vergelijken
    const eind = weekData.eind_datum > vandaagIso ? vandaagIso : weekData.eind_datum;
    const sleutel = `${weekData.start_datum}|${eind}|${filiaalFilter}`;
    if (autoRef.current.has(sleutel)) return;
    autoRef.current.add(sleutel);
    (async () => {
      const basis = {
        filiaal_id: filiaalFilter !== "all" ? filiaalFilter : undefined,
        voeg_vervangers_toe: true,
      };
      const gisteren = new Date(new Date(`${vandaagIso}T00:00:00`).getTime() - 86400000)
        .toISOString()
        .split("T")[0];
      try {
        let aanwezig = 0, afwezig = 0, vervangers = 0, verlof = 0;
        // Afgelopen dagen: volledige vergelijking (ook afwezig markeren)
        if (weekData.start_datum <= gisteren) {
          const tot = eind > gisteren ? gisteren : eind;
          const r1 = await axios.post(`${API}/planning/vergelijk-aanwezigheid`, {
            ...basis, start_datum: weekData.start_datum, eind_datum: tot, markeer_afwezig: true,
          });
          aanwezig += r1.data?.aanwezig || 0;
          afwezig += r1.data?.afwezig || 0;
          vervangers += r1.data?.vervangers || 0;
          verlof += r1.data?.verlof || 0;
        }
        // Vandaag: enkel aanwezig/vervangers (de dag is nog niet gedaan)
        if (eind >= vandaagIso) {
          const r2 = await axios.post(`${API}/planning/vergelijk-aanwezigheid`, {
            ...basis, start_datum: vandaagIso, eind_datum: vandaagIso, markeer_afwezig: false,
          });
          aanwezig += r2.data?.aanwezig || 0;
          vervangers += r2.data?.vervangers || 0;
        }
        setAutoVergeleken(
          `Automatisch vergeleken t/m ${eind} · ${aanwezig} aanwezig, ${afwezig} afwezig, ${vervangers} vervangers${verlof ? `, ${verlof} op verlof` : ""}`,
        );
        if (aanwezig + afwezig + vervangers > 0) fetchWeek();
      } catch { /* stil falen: handmatig vergelijken blijft mogelijk */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekData?.start_datum, weekData?.eind_datum, filiaalFilter]);

  // 1-klik vergelijking met Aanwezigheid
  const [vergelijken, setVergelijken] = useState(false);
  const vergelijkAanwezigheid = async () => {
    if (!weekData) return;
    autoRef.current.clear();
    if (!window.confirm(
      `Vergelijk planning van ${weekData.start_datum} → ${weekData.eind_datum} met Aanwezigheid?\n\n` +
      `• Wie ingepland was én geklokt heeft → ✅ Aanwezig\n` +
      `• Wie ingepland was maar NIET geklokt → ❌ Afwezig\n` +
      `• Wie geklokt heeft maar NIET ingepland was → toegevoegd als ➕ Vervanger`
    )) return;
    setVergelijken(true);
    try {
      const res = await axios.post(`${API}/planning/vergelijk-aanwezigheid`, {
        start_datum: weekData.start_datum,
        eind_datum: weekData.eind_datum,
        filiaal_id: filiaalFilter !== "all" ? filiaalFilter : undefined,
        voeg_vervangers_toe: true,
      });
      const d = res.data || {};
      const parts = [
        `${d.aanwezig || 0} aanwezig`,
        `${d.afwezig || 0} afwezig`,
        `${d.vervangers || 0} vervangers`,
      ];
      if (d.auto_gekoppeld) parts.push(`${d.auto_gekoppeld} auto-gekoppeld`);
      toast.success(parts.join(" · "));
      fetchWeek();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vergelijking mislukt");
    } finally {
      setVergelijken(false);
    }
  };

  // Kopieer een ingevulde cel naar de volgende dag (zelfde filiaal, zelfde medewerkers + uren)
  const copyToNextDay = async (fil, dag) => {
    if (!dag || dag.aantal === 0) return;
    const nextDate = fmtIso(addDays(new Date(dag.datum), 1));
    const slots = dag.medewerkers.map((m) => ({
      medewerker_id: m.medewerker_id,
      filiaal_id: fil.filiaal_id,
      datum: nextDate,
      uren: m.uren,
    }));
    try {
      await axios.post(`${API}/planning/cel`, {
        filiaal_id: fil.filiaal_id,
        datum: nextDate,
        slots,
      });
      toast.success(
        `Gekopieerd naar ${new Date(nextDate).toLocaleDateString("nl-BE", { day: "2-digit", month: "short" })}`
      );
      fetchWeek();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kopiëren mislukt");
    }
  };

  // Kopieer een ingevulde cel naar alle resterende dagen van de week (zelfde filiaal)
  const copyToRestOfWeek = async (fil, dag, weekDayIdx) => {
    if (!dag || dag.aantal === 0) return;
    const targetDays = [];
    for (let i = weekDayIdx + 1; i < 7; i++) {
      targetDays.push(fmtIso(addDays(new Date(dag.datum), i - weekDayIdx)));
    }
    if (targetDays.length === 0) return;
    if (!window.confirm(
      `Kopieer ${dag.aantal} medewerker(s) naar ${targetDays.length} resterende dag(en) van de week voor ${fil.filiaal_naam}?`
    )) return;
    try {
      for (const d of targetDays) {
        const slots = dag.medewerkers.map((m) => ({
          medewerker_id: m.medewerker_id,
          filiaal_id: fil.filiaal_id,
          datum: d,
          uren: m.uren,
        }));
        await axios.post(`${API}/planning/cel`, {
          filiaal_id: fil.filiaal_id,
          datum: d,
          slots,
        });
      }
      toast.success(`Gekopieerd naar ${targetDays.length} dagen`);
      fetchWeek();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kopiëren mislukt");
    }
  };

  // ── Kopieer-dialoog (dag of hele week, naar dagen en/of andere filialen) ──
  const [kopieerOpen, setKopieerOpen] = useState(false);
  const [kopBezig, setKopBezig] = useState(false);
  const [kop, setKop] = useState({
    modus: "dag", bronFiliaal: "", bronDatum: "", doelDagen: [], doelFilialen: [], doelWeek: "",
  });

  const weekDagen = Array.from({ length: 7 }).map((_, i) => fmtIso(addDays(new Date(weekStart), i)));

  const openKopieer = () => {
    const eersteFil = (weekData?.filialen || [])[0]?.filiaal_id || filialen[0]?.id || "";
    setKop({
      modus: "dag",
      bronFiliaal: eersteFil,
      bronDatum: weekDagen[0],
      doelDagen: [],
      doelFilialen: [],
      doelWeek: fmtIso(addDays(new Date(weekStart), 7)),
    });
    setKopieerOpen(true);
  };

  const toggleIn = (lijst, waarde) =>
    lijst.includes(waarde) ? lijst.filter((x) => x !== waarde) : [...lijst, waarde];

  const doeKopieer = async () => {
    if (!kop.bronFiliaal) { toast.error("Kies een bronfiliaal"); return; }
    const body = {
      bron_filiaal_id: kop.bronFiliaal,
      doel_filiaal_ids: kop.doelFilialen,
    };
    if (kop.modus === "dag") {
      if (!kop.doelDagen.length && !kop.doelFilialen.length) {
        toast.error("Kies minstens één doeldag of een ander filiaal");
        return;
      }
      body.bron_datum = kop.bronDatum;
      body.doel_datums = kop.doelDagen.length ? kop.doelDagen : [kop.bronDatum];
    } else {
      body.bron_week_start = weekStart;
      body.doel_week_start = kop.doelWeek;
    }
    setKopBezig(true);
    try {
      const { data } = await axios.post(`${API}/planning/kopieer`, body);
      toast.success(`${data.aantal} regel(s) gekopieerd naar ${data.cellen} dag(en)`);
      setKopieerOpen(false);
      fetchWeek();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kopiëren mislukt");
    } finally {
      setKopBezig(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="planning-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className="text-2xl font-black tracking-tight flex items-center gap-2"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            <CalendarBlank size={24} weight="bold" className="text-[#002FA7]" />
            Planning
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Wie werkt waar — alleen informatief, los van Aanwezigheid en alle andere modules.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => { if (view === "dag") fetchDag(); else fetchWeek(); }}
            title="Vernieuwen"
            data-testid="refresh-btn"
          >
            <ArrowClockwise size={16} weight="bold" />
          </Button>
          <Button
            variant={editMode ? "default" : "outline"}
            onClick={() => setEditMode(!editMode)}
            className={editMode ? "bg-[#002FA7] hover:bg-[#002280] text-white" : ""}
            data-testid="edit-mode-btn"
          >
            <PencilSimple size={16} weight="bold" className="mr-1" />
            {editMode ? "Klaar" : "Bewerken"}
          </Button>
          {editMode && view === "week" && weekData && (
            <Button
              variant="outline"
              onClick={vergelijkAanwezigheid}
              disabled={vergelijken}
              data-testid="vergelijk-btn"
              title="Opnieuw vergelijken met de werkelijke aanwezigheid (gebeurt ook automatisch bij openen)"
              className="border-[#002FA7] text-[#002FA7] hover:bg-[#EAF1FF]"
            >
              <Scales size={16} weight="bold" className="mr-1" />
              {vergelijken ? "Vergelijken..." : "Vergelijk nu"}
            </Button>
          )}
          {editMode && view === "week" && weekData && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm(
                  `Importeer handmatig ingevoerde aanwezigheid van deze week (${weekData.start_datum} → ${weekData.eind_datum}) als startpunt voor de planning?\n\nBestaande planning-records worden niet vervangen.`
                )) return;
                try {
                  const res = await axios.post(`${API}/planning/import-from-aanwezigheid`, {
                    start_datum: weekData.start_datum,
                    eind_datum: weekData.eind_datum,
                  });
                  toast.success(`${res.data.aangemaakt || 0} geïmporteerd · ${res.data.overgeslagen || 0} overgeslagen`);
                  fetchWeek();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Import mislukt");
                }
              }}
              data-testid="import-btn"
              title="Eenmalige import vanuit Aanwezigheid (handmatige uren)"
            >
              <ArrowClockwise size={16} weight="bold" className="mr-1" />
              Importeer
            </Button>
          )}
          {view === "week" && weekData && (
            <Button
              variant="outline"
              onClick={openKopieer}
              className="border-[#002FA7] text-[#002FA7] hover:bg-[#EAF1FF]"
              title="Kopieer een dag of een hele week naar andere dagen of filialen"
              data-testid="kopieer-btn"
            >
              <CopySimple size={16} weight="bold" className="mr-1" />
              Kopieer
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => dl("/planning/week/excel", `planning_${weekStart}.xlsx`)}
            disabled={!weekData || view !== "week"}
            title="Excel met één werkblad per filiaal"
            data-testid="export-excel-btn"
          >
            <FileXls size={16} weight="bold" className="mr-1" /> Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => dl("/planning/week/pdf", `planning_${weekStart}.pdf`)}
            disabled={!weekData || view !== "week"}
            title="PDF met één pagina per filiaal"
            data-testid="export-pdf-btn"
          >
            <FilePdf size={16} weight="bold" className="mr-1" /> PDF
          </Button>
        </div>
      </div>

      {/* Status legend */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-[11px] sm:text-xs bg-[#F4F5F7] border border-border rounded-sm px-3 py-2" data-testid="status-legend">
        <span className="font-bold uppercase tracking-wider text-muted-foreground text-[10px]">Statussen:</span>
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
          const Icon = cfg.icon;
          return (
            <span key={key} className="flex items-center gap-1.5 px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: cfg.bg }}>
              <Icon size={13} weight={cfg.iconWeight} color={cfg.color} />
              <span className={`${cfg.nameClass} font-semibold`}>{cfg.label}</span>
            </span>
          );
        })}
        <span className="flex items-center gap-1.5 text-[11px] bg-[#FFF8E1] border border-[#F9A825]/40 rounded-sm px-1.5 py-0.5">
          <Airplane size={12} weight="fill" color="#8a5a00" />
          <span className="font-semibold text-[#8a5a00]">Verlof</span>
        </span>
        <span className="flex items-center gap-1.5 text-[11px] bg-[#FFF4E5] border border-[#B45309]/40 rounded-sm px-1.5 py-0.5">
          <ClockClockwise size={12} weight="bold" color="#B45309" />
          <span className="font-semibold text-[#B45309]">Geklokt, niet gepland</span>
        </span>
        <span className="flex items-center gap-1.5 text-[11px]">
          <span className="bg-[#DCFCE7] text-[#15803D] border border-[#15803D]/30 rounded-sm px-1 font-bold">+u</span>
          <span className="font-semibold">overuren</span>
          <span className="bg-[#FEE2E2] text-[#B91C1C] border border-[#B91C1C]/30 rounded-sm px-1 font-bold">−u</span>
          <span className="font-semibold">minuren</span>
        </span>
        {autoVergeleken && (
          <span className="text-[11px] font-semibold text-[#38A169]" data-testid="auto-vergelijk-info">
            {autoVergeleken}
          </span>
        )}
        {editMode && (
          <span className="text-muted-foreground italic ml-auto">
            Klik op het icoon naast een naam om de status te wijzigen
          </span>
        )}
      </div>

      {/* View toggle */}
      <div className="grid grid-cols-2 gap-1 p-1 bg-muted rounded-sm max-w-md" data-testid="view-toggle">
        <button
          onClick={() => setView("dag")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            view === "dag" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="view-dag"
        >
          <ListBullets size={14} weight="bold" /> Dag
        </button>
        <button
          onClick={() => setView("week")}
          className={`flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider py-2.5 rounded-sm transition-colors ${
            view === "week" ? "bg-white text-[#002FA7] shadow-sm" : "text-muted-foreground"
          }`}
          data-testid="view-week"
        >
          <TableIcon size={14} weight="bold" /> Week
        </button>
      </div>

      {/* Filiaal filter */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
        <div>
          <Label className="text-xs font-semibold flex items-center gap-1">
            <Buildings size={12} weight="bold" /> Filiaal
          </Label>
          <Select value={filiaalFilter} onValueChange={setFiliaalFilter}>
            <SelectTrigger className="mt-1.5" data-testid="filiaal-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle filialen</SelectItem>
              {filialen.map((f) => (<SelectItem key={f.id} value={f.id}>{f.naam}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* ═══ Dag view ═══ */}
      {view === "dag" && (
        <div className="space-y-4" data-testid="dag-card">
          <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
            <Button size="icon" variant="outline" onClick={() => navDag(-1)} data-testid="dag-prev"><CaretLeft size={16} weight="bold" /></Button>
            <Input type="date" value={datum} onChange={(e) => setDatum(e.target.value)} className="flex-1 sm:w-44" data-testid="dag-datum" />
            <Button size="icon" variant="outline" onClick={() => navDag(1)} data-testid="dag-next"><CaretRight size={16} weight="bold" /></Button>
            <Button variant="ghost" size="sm" onClick={goToday} data-testid="dag-vandaag">Vandaag</Button>
            <div className="flex-1 text-right">
              {dagData && (
                <span className="text-sm">
                  <span className="font-bold">{fmtDateLang(datum)}</span>
                  {" · "}
                  <span className="text-[#002FA7] font-bold">{dagData.totaal_aantal} medewerkers</span>
                  {" actief"}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground py-12 text-center">Laden...</div>
          ) : !dagData ? null : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {visibleFilialen(dagData.filialen).map((f) => (
                <div
                  key={f.filiaal_id}
                  className="bg-white border border-border rounded-sm p-4"
                  data-testid={`dag-filiaal-${f.filiaal_id}`}
                >
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-border">
                    <h3 className="font-bold text-sm flex items-center gap-1.5">
                      <Buildings size={14} weight="bold" className="text-[#002FA7]" />
                      {f.filiaal_naam}
                    </h3>
                    <span className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded-sm ${
                      f.aantal === 0 ? "bg-[#FEE2E2] text-[#E53E3E]" : "bg-[#D1FAE5] text-[#065F46]"
                    }`}>
                      {f.aantal}p · {f.totaal_uren.toFixed(1).replace(".", ",")}u
                    </span>
                  </div>
                  {f.medewerkers.length === 0 && !(f.verlof || []).length ? (
                    <p className="text-xs text-muted-foreground italic py-2 text-center">Niemand gepland</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {f.medewerkers.map((m) => (
                        <li
                          key={m.medewerker_id + Math.random()}
                          className="flex items-center justify-between text-xs bg-[#EAF1FF] rounded-sm px-2 py-1.5"
                        >
                          <span className="font-semibold truncate flex-1">{m.medewerker_naam}</span>
                          {m.verlof_type && (
                            <span className="text-[9px] font-bold uppercase tracking-wider bg-[#F9A825]/20 text-[#8a5a00] border border-[#F9A825]/40 rounded-sm px-1 shrink-0" title={`Verlof: ${m.verlof_type}`}>
                              {m.verlof_type}
                            </span>
                          )}
                          <span className="font-bold text-[#002FA7] tabular-nums whitespace-nowrap ml-2">
                            {m.uren.toFixed(1).replace(".", ",")}u
                          </span>
                        </li>
                      ))}
                      {(f.verlof || []).map((v) => (
                        <li
                          key={`verlof-${v.medewerker_id}`}
                          className="flex items-center gap-1.5 text-xs bg-[#FFF8E1] border border-[#F9A825]/40 rounded-sm px-2 py-1.5"
                          data-testid={`dag-verlof-${f.filiaal_id}-${v.medewerker_id}`}
                        >
                          <Airplane size={13} weight="fill" color="#8a5a00" className="shrink-0" />
                          <span className="font-semibold truncate flex-1 text-[#8a5a00]">{v.medewerker_naam}</span>
                          <span className="text-[9px] font-bold uppercase tracking-wider text-[#8a5a00] shrink-0">
                            {v.verlof_type}{v.halve_dag ? " ½" : ""}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ═══ Week view ═══ */}
      {view === "week" && (
        <div className="space-y-4" data-testid="week-card">
          <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
            <Button size="icon" variant="outline" onClick={() => navWeek(-1)} data-testid="week-prev"><CaretLeft size={16} weight="bold" /></Button>
            <Input type="date" value={weekStart} onChange={(e) => setWeekStart(fmtIso(mondayOf(new Date(e.target.value))))} className="flex-1 sm:w-44" data-testid="week-start" />
            <Button size="icon" variant="outline" onClick={() => navWeek(1)} data-testid="week-next"><CaretRight size={16} weight="bold" /></Button>
            <Button variant="ghost" size="sm" onClick={goToday} data-testid="week-vandaag">Deze week</Button>
            <div className="flex-1 text-right text-sm">
              {weekData && (
                <span>
                  <span className="font-bold">Week van {fmtDateLang(weekData.start_datum)}</span>
                  {" t/m "}
                  <span className="font-bold">{fmtDateLang(weekData.eind_datum)}</span>
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground py-12 text-center">Laden...</div>
          ) : !weekData ? null : (
            <div>
              <p className="md:hidden text-[10px] text-muted-foreground mb-1 font-semibold">↔ Veeg zijwaarts om alle dagen te zien</p>
              <div className="bg-white border border-[#999] rounded-sm overflow-x-auto">
              <table className="w-full text-xs border-collapse" style={{ minWidth: "1100px" }}>
                <thead>
                  <tr className="bg-[#E0FFFF]">
                    <th className="px-3 py-2 text-left font-bold border border-[#999] sticky left-0 bg-[#E0FFFF] z-10" style={{ width: "180px" }}>Filiaal</th>
                    {Array.from({ length: 7 }).map((_, i) => {
                      const dt = addDays(new Date(weekData.start_datum), i);
                      let bgClass = "bg-[#E0FFFF]";
                      if (i === 6) bgClass = "bg-[#FF6666] text-white";
                      else if (i === 5) bgClass = "bg-[#00CED1] text-white";
                      return (
                        <th
                          key={i}
                          className={`px-2 py-2 text-center font-bold border border-[#999] ${bgClass}`}
                          style={{ minWidth: "140px" }}
                        >
                          <div className="text-[10px] opacity-80">{DAGNAMEN_LANG[i]}</div>
                          <div>{fmtDate(fmtIso(dt))}</div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {visibleFilialen(weekData.filialen).map((fil) => (
                    <tr key={fil.filiaal_id} data-testid={`week-row-${fil.filiaal_id}`}>
                      <td className="px-3 py-2 font-bold sticky left-0 bg-white z-10 border border-[#999]" style={{ width: "180px" }}>
                        <div className="flex items-center gap-1.5">
                          <span className="truncate flex-1" title={fil.filiaal_naam}>{fil.filiaal_naam}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); dlFiliaal("/planning/week/excel", fil, "xlsx"); }}
                            title={`Excel van ${fil.filiaal_naam}`}
                            className="w-6 h-6 flex items-center justify-center text-[#16794C] border border-[#16794C]/30 rounded-sm active:bg-[#DCFCE7] shrink-0"
                            data-testid={`fil-excel-${fil.filiaal_id}`}
                          >
                            <FileXls size={13} weight="bold" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); dlFiliaal("/planning/week/pdf", fil, "pdf"); }}
                            title={`PDF van ${fil.filiaal_naam}`}
                            className="w-6 h-6 flex items-center justify-center text-[#B91C1C] border border-[#B91C1C]/30 rounded-sm active:bg-[#FEE2E2] shrink-0"
                            data-testid={`fil-pdf-${fil.filiaal_id}`}
                          >
                            <FilePdf size={13} weight="bold" />
                          </button>
                        </div>
                        {(fil.saldo_totaal?.saldo || 0) !== 0 && (
                          <div
                            className={`mt-1 text-[10px] font-bold tabular-nums ${
                              fil.saldo_totaal.saldo > 0 ? "text-[#15803D]" : "text-[#B91C1C]"
                            }`}
                            data-testid={`fil-saldo-${fil.filiaal_id}`}
                          >
                            Saldo {fil.saldo_totaal.saldo > 0 ? "+" : ""}
                            {fil.saldo_totaal.saldo.toFixed(2).replace(".", ",")}u
                          </div>
                        )}
                      </td>
                      {fil.dagen.map((dag, di) => {
                        const isWk = di >= 5;
                        let cellBg = "bg-white";
                        if (di === 6) cellBg = "bg-[#FFEBEB]";
                        else if (di === 5) cellBg = "bg-[#E0F7F9]";
                        const editableCls = editMode
                          ? "cursor-pointer hover:bg-[#EAF1FF] hover:ring-2 hover:ring-[#002FA7] hover:ring-inset transition-all"
                          : "";
                        return (
                          <td
                            key={di}
                            className={`px-2 py-2 align-top border border-[#999] ${cellBg} ${editableCls}`}
                            style={{ minWidth: "140px" }}
                            onClick={() => openEditCel(fil, dag)}
                            data-testid={`cell-${fil.filiaal_id}-${dag.datum}`}
                          >
                            {dag.aantal === 0 && !(dag.verlof || []).length && !(dag.extra || []).length ? (
                              <span className="text-muted-foreground italic">
                                {editMode ? "+ Toevoegen" : "—"}
                              </span>
                            ) : (
                              <div>
                                <div className="flex items-center justify-between mb-1.5">
                                  <div className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm ${
                                    dag.aantal === 0
                                      ? "bg-[#F9A825]/20 text-[#8a5a00]"
                                      : isWk ? "bg-white text-[#002FA7]" : "bg-[#002FA7] text-white"
                                  }`}>
                                    {dag.aantal === 0
                                      ? `${(dag.verlof || []).length + (dag.extra || []).length}× niet gepland`
                                      : `${dag.aantal}p · ${dag.totaal_uren.toFixed(1).replace(".", ",")}u`}
                                  </div>
                                  {editMode && (
                                    <div className="flex gap-0.5" onClick={(e) => e.stopPropagation()}>
                                      {di < 6 && (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); copyToNextDay(fil, dag); }}
                                          title={`Kopieer naar ${DAGNAMEN_LANG[di + 1] || "volgende dag"}`}
                                          className="w-5 h-5 flex items-center justify-center text-[#002FA7] hover:bg-[#EAF1FF] rounded-sm"
                                          data-testid={`copy-next-${fil.filiaal_id}-${dag.datum}`}
                                        >
                                          <ArrowRight size={11} weight="bold" />
                                        </button>
                                      )}
                                      {di < 5 && (
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); copyToRestOfWeek(fil, dag, di); }}
                                          title="Kopieer naar rest van de week"
                                          className="w-5 h-5 flex items-center justify-center text-[#002FA7] hover:bg-[#EAF1FF] rounded-sm"
                                          data-testid={`copy-week-${fil.filiaal_id}-${dag.datum}`}
                                        >
                                          <CopySimple size={11} weight="bold" />
                                        </button>
                                      )}
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); clearCel(fil, dag); }}
                                        title="Verwijder alle medewerkers uit deze cel"
                                        className="w-5 h-5 flex items-center justify-center text-muted-foreground hover:bg-[#FFEBEB] hover:text-[#E53E3E] rounded-sm"
                                        data-testid={`clear-cel-${fil.filiaal_id}-${dag.datum}`}
                                      >
                                        <Trash size={11} weight="bold" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <ul className="space-y-1">
                                  {dag.medewerkers.map((m, mi) => {
                                    const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.gepland;
                                    const StatusIcon = cfg.icon;
                                    const vervangerVoor = m.status === "vervanger" && m.vervanger_voor_id
                                      ? dag.medewerkers.find((x) => x.medewerker_id === m.vervanger_voor_id)
                                      : null;
                                    return (
                                      <li
                                        key={`${m.medewerker_id}-${mi}`}
                                        className={`text-[11px] flex items-center gap-1 px-1 py-0.5 rounded-sm flex-wrap`}
                                        style={{ backgroundColor: cfg.bg }}
                                        data-testid={`mw-${fil.filiaal_id}-${dag.datum}-${m.medewerker_id}`}
                                      >
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (editMode) cycleStatus(m);
                                          }}
                                          title={`${cfg.label}${editMode ? " — klik om te wijzigen" : ""}`}
                                          className={`shrink-0 ${editMode ? "hover:scale-125 transition-transform cursor-pointer" : "cursor-default"}`}
                                          data-testid={`status-${m.id}`}
                                        >
                                          <StatusIcon size={13} weight={cfg.iconWeight} color={cfg.color} />
                                        </button>
                                        <span className={`font-semibold ${cfg.nameClass}`}>{m.medewerker_naam}</span>
                                        <span className={`text-muted-foreground ${cfg.nameClass}`}> · {m.uren.toFixed(1).replace(".", ",")}u</span>
                                        {m.verschil_uren !== null && m.verschil_uren !== undefined && Math.abs(m.verschil_uren) >= 0.01 && (
                                          <span
                                            className={`text-[10px] font-bold tabular-nums rounded-sm px-1 shrink-0 ${
                                              m.verschil_uren > 0
                                                ? "bg-[#DCFCE7] text-[#15803D] border border-[#15803D]/30"
                                                : "bg-[#FEE2E2] text-[#B91C1C] border border-[#B91C1C]/30"
                                            }`}
                                            title={`Gepland ${m.uren.toFixed(2).replace(".", ",")}u · gewerkt ${(m.werkelijke_uren || 0).toFixed(2).replace(".", ",")}u`}
                                            data-testid={`verschil-${m.id}`}
                                          >
                                            {m.verschil_uren > 0 ? "+" : ""}
                                            {m.verschil_uren.toFixed(2).replace(".", ",").replace(/,00$/, "")}u
                                          </span>
                                        )}
                                        {m.verlof_type && (
                                          <span
                                            className="text-[9px] font-bold uppercase tracking-wider bg-[#F9A825]/20 text-[#8a5a00] border border-[#F9A825]/40 rounded-sm px-1 shrink-0"
                                            title={`Deze medewerker heeft verlof (${m.verlof_type})`}
                                            data-testid={`verlof-tag-${m.id}`}
                                          >
                                            {m.verlof_type}
                                          </span>
                                        )}
                                        {vervangerVoor && (
                                          <span
                                            className="text-[10px] italic text-[#2563EB] w-full pl-4"
                                            title={`Vervanger voor ${vervangerVoor.medewerker_naam}`}
                                          >
                                            ↳ voor {vervangerVoor.medewerker_naam}
                                          </span>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                                {(dag.extra || []).length > 0 && (
                                  <ul className="space-y-1 mt-1">
                                    {dag.extra.map((e) => (
                                      <li
                                        key={`extra-${e.medewerker_id}`}
                                        className="text-[11px] flex items-center gap-1 px-1 py-0.5 rounded-sm bg-[#FFF4E5] border border-[#B45309]/40"
                                        data-testid={`extra-${fil.filiaal_id}-${dag.datum}-${e.medewerker_id}`}
                                      >
                                        <ClockClockwise size={12} weight="bold" color="#B45309" className="shrink-0" />
                                        <span className="font-semibold text-[#B45309]">{e.medewerker_naam}</span>
                                        <span className="text-[9px] text-[#B45309]">niet gepland</span>
                                        <span className="text-[10px] font-bold tabular-nums bg-[#DCFCE7] text-[#15803D] border border-[#15803D]/30 rounded-sm px-1 ml-auto">
                                          +{e.werkelijke_uren.toFixed(2).replace(".", ",").replace(/,00$/, "")}u
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                                {(dag.verlof || []).length > 0 && (
                                  <ul className="space-y-1 mt-1">
                                    {dag.verlof.map((v) => (
                                      <li
                                        key={`verlof-${v.medewerker_id}`}
                                        className="text-[11px] flex items-center gap-1 px-1 py-0.5 rounded-sm bg-[#FFF8E1] border border-[#F9A825]/40"
                                        data-testid={`verlof-${fil.filiaal_id}-${dag.datum}-${v.medewerker_id}`}
                                      >
                                        <Airplane size={12} weight="fill" color="#8a5a00" className="shrink-0" />
                                        <span className="font-semibold text-[#8a5a00]">{v.medewerker_naam}</span>
                                        <span className="text-[9px] font-bold uppercase tracking-wider text-[#8a5a00]">
                                          {v.verlof_type}{v.halve_dag ? " ½" : ""}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {/* Totalen rij */}
                  <tr className="bg-[#EAF1FF] font-bold">
                    <td className="px-3 py-2 sticky left-0 bg-[#EAF1FF] z-10 border border-[#999] text-[#002FA7]">
                      <Users size={12} weight="bold" className="inline mr-1" />
                      TOTAAL
                    </td>
                    {weekData.totalen_per_dag.map((t, i) => (
                      <td key={i} className="px-2 py-2 text-center border border-[#999] tabular-nums">
                        <div className="text-[#002FA7]">{t.aantal}p</div>
                        <div className="text-[10px] text-muted-foreground">{t.totaal_uren.toFixed(1).replace(".", ",")}u</div>
                        {!!t.totaal_werkelijk && (
                          <div className="text-[10px] text-muted-foreground font-normal">
                            gewerkt {t.totaal_werkelijk.toFixed(1).replace(".", ",")}u
                          </div>
                        )}
                        {Math.abs(t.totaal_verschil || 0) >= 0.01 && (
                          <div className={`text-[10px] font-bold ${t.totaal_verschil > 0 ? "text-[#15803D]" : "text-[#B91C1C]"}`}>
                            {t.totaal_verschil > 0 ? "+" : ""}{t.totaal_verschil.toFixed(1).replace(".", ",")}u
                          </div>
                        )}
                        {!!t.aantal_verlof && (
                          <div className="text-[10px] font-bold text-[#8a5a00]">{t.aantal_verlof}× verlof</div>
                        )}
                        {!!t.aantal_extra && (
                          <div className="text-[10px] font-bold text-[#B45309]">{t.aantal_extra}× niet gepland</div>
                        )}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Over-/minuren per medewerker (week) */}
            {(weekData.week_saldo || []).length > 0 && (
              <div className="mt-4 bg-white border border-border rounded-sm p-3 sm:p-4" data-testid="week-saldo-card">
                <div className="flex items-center justify-between gap-2 mb-2 pb-2 border-b border-border">
                  <h3 className="font-bold text-sm flex items-center gap-1.5">
                    <Scales size={15} weight="bold" className="text-[#002FA7]" />
                    Over-/minuren deze week
                  </h3>
                  <span
                    className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded-sm ${
                      (weekData.saldo_totaal?.saldo || 0) > 0.01
                        ? "bg-[#DCFCE7] text-[#15803D]"
                        : (weekData.saldo_totaal?.saldo || 0) < -0.01
                        ? "bg-[#FEE2E2] text-[#B91C1C]"
                        : "bg-muted text-muted-foreground"
                    }`}
                    data-testid="saldo-totaal"
                  >
                    Totaal {(weekData.saldo_totaal?.saldo || 0) > 0 ? "+" : ""}
                    {(weekData.saldo_totaal?.saldo || 0).toFixed(2).replace(".", ",")}u
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground mb-2">
                  Vergelijking gepland (planning) vs. gewerkt (aanwezigheid). Verlofdagen zonder klokking tellen niet mee.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-[#EAF1FF] text-[#002FA7]">
                        <th className="px-2 py-1.5 text-left font-bold border border-[#CBD5E1]">Medewerker</th>
                        <th className="px-2 py-1.5 text-right font-bold border border-[#CBD5E1]">Gepland</th>
                        <th className="px-2 py-1.5 text-right font-bold border border-[#CBD5E1]">Gewerkt</th>
                        <th className="px-2 py-1.5 text-right font-bold border border-[#CBD5E1]">Saldo</th>
                        <th className="px-2 py-1.5 text-center font-bold border border-[#CBD5E1]">Verlof</th>
                      </tr>
                    </thead>
                    <tbody>
                      {weekData.week_saldo.map((r) => (
                        <tr key={r.medewerker_id} data-testid={`saldo-rij-${r.medewerker_id}`}>
                          <td className="px-2 py-1.5 border border-[#E2E8F0] font-semibold">{r.medewerker_naam || "—"}</td>
                          <td className="px-2 py-1.5 border border-[#E2E8F0] text-right tabular-nums">{r.gepland.toFixed(2).replace(".", ",")}u</td>
                          <td className="px-2 py-1.5 border border-[#E2E8F0] text-right tabular-nums">{r.gewerkt.toFixed(2).replace(".", ",")}u</td>
                          <td
                            className={`px-2 py-1.5 border border-[#E2E8F0] text-right tabular-nums font-bold ${
                              r.saldo > 0.01
                                ? "bg-[#DCFCE7] text-[#15803D]"
                                : r.saldo < -0.01
                                ? "bg-[#FEE2E2] text-[#B91C1C]"
                                : "text-muted-foreground"
                            }`}
                          >
                            {r.saldo > 0 ? "+" : ""}{r.saldo.toFixed(2).replace(".", ",")}u
                          </td>
                          <td className="px-2 py-1.5 border border-[#E2E8F0] text-center tabular-nums text-muted-foreground">
                            {r.verlof_dagen || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </div>
          )}
        </div>
      )}

      {/* Edit Cell Dialog */}
      <Dialog open={!!editCel} onOpenChange={(o) => !o && setEditCel(null)}>
        <DialogContent data-testid="edit-cel-dialog">
          <DialogHeader>
            <DialogTitle>Wie werkt op {editCel ? new Date(editCel.datum).toLocaleDateString("nl-BE", { day: "2-digit", month: "long" }) : ""}?</DialogTitle>
            <DialogDescription>
              {editCel?.filiaal_naam} — {editCel ? DAGNAMEN_LANG[editCel.weekdag] : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {editCel?.slots.map((slot, idx) => {
              const cfg = STATUS_CONFIG[slot.status || "gepland"];
              const StatusIcon = cfg.icon;
              const afwezigen = (editCel.slots || [])
                .filter((s) => s.status === "afwezig" && s.medewerker_id);
              return (
                <div key={idx} className="border border-border rounded-sm p-2 space-y-2" style={{ backgroundColor: cfg.bg }}>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => cellSetSlot(idx, "status", nextStatus(slot.status))}
                      className="w-9 h-9 flex items-center justify-center border border-border rounded-sm bg-white hover:bg-muted shrink-0"
                      title={`${cfg.label} — klik om te wijzigen`}
                      data-testid={`slot-${idx}-status`}
                    >
                      <StatusIcon size={16} weight={cfg.iconWeight} color={cfg.color} />
                    </button>
                    <Select
                      value={slot.medewerker_id}
                      onValueChange={(v) => cellSetSlot(idx, "medewerker_id", v)}
                    >
                      <SelectTrigger className="flex-1 h-9 text-xs" data-testid={`slot-${idx}-med`}>
                        <SelectValue placeholder="Selecteer medewerker..." />
                      </SelectTrigger>
                      <SelectContent>
                        {medewerkers.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.voornaam} {m.achternaam}
                            {celVerlof[m.id] ? ` — verlof (${celVerlof[m.id].type})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number" step="0.25" min="0" max="24"
                      value={slot.uren}
                      onChange={(e) => cellSetSlot(idx, "uren", e.target.value)}
                      className="w-20 h-9 text-xs tabular-nums"
                      placeholder="u"
                      data-testid={`slot-${idx}-uren`}
                    />
                    <button
                      type="button"
                      onClick={() => cellRemoveSlot(idx)}
                      className="w-9 h-9 flex items-center justify-center text-muted-foreground hover:text-[#E53E3E] border border-border rounded-sm bg-white"
                      data-testid={`slot-${idx}-wis`}
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </div>
                  {celVerlof[slot.medewerker_id] && (
                    <div
                      className="flex items-start gap-1.5 text-[11px] font-semibold text-[#8a5a00] bg-[#FFF8E1] border border-[#F9A825]/50 rounded-sm px-2 py-1.5"
                      data-testid={`slot-${idx}-verlof-warning`}
                    >
                      <Warning size={14} weight="fill" className="shrink-0 mt-[1px]" />
                      <span>
                        Deze medewerker heeft verlof op deze dag
                        {" "}({celVerlof[slot.medewerker_id].type}
                        {celVerlof[slot.medewerker_id].halve_dag ? ", ½ dag" : ""}).
                      </span>
                    </div>
                  )}
                  {slot.status === "vervanger" && afwezigen.length > 0 && (
                    <div className="flex items-center gap-2 pl-11">
                      <span className="text-[10px] text-[#2563EB] font-bold uppercase tracking-wider whitespace-nowrap">
                        Vervanger voor:
                      </span>
                      <Select
                        value={slot.vervanger_voor_id || "_geen"}
                        onValueChange={(v) =>
                          cellSetSlot(idx, "vervanger_voor_id", v === "_geen" ? null : v)
                        }
                      >
                        <SelectTrigger className="flex-1 h-8 text-xs" data-testid={`slot-${idx}-voor`}>
                          <SelectValue placeholder="Kies afwezige..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_geen">— niet gekoppeld —</SelectItem>
                          {afwezigen.map((a) => {
                            const med = medewerkers.find((m) => m.id === a.medewerker_id);
                            return (
                              <SelectItem key={a.medewerker_id} value={a.medewerker_id}>
                                {med ? `${med.voornaam} ${med.achternaam}` : a.medewerker_id}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
              );
            })}
            {(!editCel?.slots || editCel.slots.length === 0) && (
              <p className="text-xs text-muted-foreground italic text-center py-4">
                Nog geen medewerkers gepland.
              </p>
            )}
          </div>
          <Button
            variant="outline"
            onClick={cellAddSlot}
            className="w-full"
            data-testid="add-slot-btn"
          >
            <Plus size={14} weight="bold" className="mr-1" />
            Medewerker toevoegen
          </Button>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditCel(null)} data-testid="cel-cancel">
              Annuleren
            </Button>
            <Button
              onClick={saveEditCel}
              className="bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="cel-save"
            >
              <Check size={14} weight="bold" className="mr-1" /> Opslaan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ═══ Kopieer-dialoog ═══ */}
      <Dialog open={kopieerOpen} onOpenChange={setKopieerOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="kopieer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CopySimple size={18} weight="bold" className="text-[#002FA7]" />
              Rooster kopiëren
            </DialogTitle>
            <DialogDescription className="text-xs">
              Kopieer één dag of de hele week naar andere dagen en/of andere filialen.
              De doeldagen worden overschreven.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-1 p-1 bg-muted rounded-sm">
              {[
                { k: "dag", l: "Eén dag" },
                { k: "week", l: "Hele week" },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setKop((p) => ({ ...p, modus: m.k }))}
                  className={`py-2 text-xs font-bold rounded-sm transition-colors ${
                    kop.modus === m.k ? "bg-[#002FA7] text-white" : "text-muted-foreground"
                  }`}
                  data-testid={`kopieer-modus-${m.k}`}
                >
                  {m.l}
                </button>
              ))}
            </div>

            <div>
              <Label className="text-xs font-semibold">Van filiaal</Label>
              <Select
                value={kop.bronFiliaal}
                onValueChange={(v) => setKop((p) => ({ ...p, bronFiliaal: v }))}
              >
                <SelectTrigger className="mt-1 h-9" data-testid="kopieer-bron-filiaal">
                  <SelectValue placeholder="Kies filiaal..." />
                </SelectTrigger>
                <SelectContent>
                  {(weekData?.filialen || []).map((f) => (
                    <SelectItem key={f.filiaal_id} value={f.filiaal_id}>{f.filiaal_naam}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {kop.modus === "dag" ? (
              <>
                <div>
                  <Label className="text-xs font-semibold">Van dag</Label>
                  <Select
                    value={kop.bronDatum}
                    onValueChange={(v) => setKop((p) => ({ ...p, bronDatum: v, doelDagen: p.doelDagen.filter((d) => d !== v) }))}
                  >
                    <SelectTrigger className="mt-1 h-9" data-testid="kopieer-bron-dag">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {weekDagen.map((d, i) => (
                        <SelectItem key={d} value={d}>{DAGNAMEN_LANG[i]} {fmtDate(d)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold">Naar dag(en)</Label>
                    <button
                      type="button"
                      className="text-[11px] font-bold text-[#002FA7] underline"
                      onClick={() =>
                        setKop((p) => ({ ...p, doelDagen: weekDagen.filter((d) => d !== p.bronDatum) }))
                      }
                      data-testid="kopieer-alle-dagen"
                    >
                      Alle andere dagen
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 mt-1.5">
                    {weekDagen.map((d, i) => {
                      const isBron = d === kop.bronDatum;
                      const aan = kop.doelDagen.includes(d);
                      return (
                        <button
                          key={d}
                          type="button"
                          disabled={isBron}
                          onClick={() => setKop((p) => ({ ...p, doelDagen: toggleIn(p.doelDagen, d) }))}
                          className={`px-2 py-2 text-[11px] font-bold rounded-sm border transition-colors ${
                            isBron
                              ? "bg-muted text-muted-foreground border-border"
                              : aan
                              ? "bg-[#002FA7] text-white border-[#002FA7]"
                              : "bg-white border-border hover:bg-[#EAF1FF]"
                          }`}
                          data-testid={`kopieer-doel-dag-${i}`}
                        >
                          {DAGNAMEN_KORT[i]} {fmtDate(d)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div>
                <Label className="text-xs font-semibold">Naar week (maandag)</Label>
                <Input
                  type="date"
                  value={kop.doelWeek}
                  onChange={(e) =>
                    setKop((p) => ({ ...p, doelWeek: fmtIso(mondayOf(new Date(e.target.value))) }))
                  }
                  className="mt-1 h-9"
                  data-testid="kopieer-doel-week"
                />
                <p className="text-[10px] text-muted-foreground mt-1">
                  De hele week van {fmtDateLang(weekStart)} wordt gekopieerd.
                </p>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold">Naar ander filiaal (optioneel)</Label>
              <p className="text-[10px] text-muted-foreground">
                Niets aanvinken = binnen hetzelfde filiaal kopiëren.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-1.5">
                {(weekData?.filialen || [])
                  .filter((f) => f.filiaal_id !== kop.bronFiliaal)
                  .map((f) => {
                    const aan = kop.doelFilialen.includes(f.filiaal_id);
                    return (
                      <button
                        key={f.filiaal_id}
                        type="button"
                        onClick={() =>
                          setKop((p) => ({ ...p, doelFilialen: toggleIn(p.doelFilialen, f.filiaal_id) }))
                        }
                        className={`px-2 py-2 text-[11px] font-bold rounded-sm border text-left truncate ${
                          aan
                            ? "bg-[#002FA7] text-white border-[#002FA7]"
                            : "bg-white border-border hover:bg-[#EAF1FF]"
                        }`}
                        data-testid={`kopieer-doel-filiaal-${f.filiaal_id}`}
                      >
                        {f.filiaal_naam}
                      </button>
                    );
                  })}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setKopieerOpen(false)} data-testid="kopieer-cancel">
              Annuleren
            </Button>
            <Button
              onClick={doeKopieer}
              disabled={kopBezig}
              className="bg-[#002FA7] hover:bg-[#002280] text-white"
              data-testid="kopieer-save"
            >
              <CopySimple size={14} weight="bold" className="mr-1" />
              {kopBezig ? "Kopiëren..." : "Kopiëren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
